import { NgTemplateOutlet } from "@angular/common";
import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    output,
    signal,
    ViewEncapsulation,
} from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { CerrixTreeItem } from "@models/tree/CerrixTreeItem";
import { CerrixCheckboxComponent } from "../../atoms/cerrix-checkbox/cerrix-checkbox.component";
import { CerrixRadioComponent } from "../../atoms/cerrix-radio/cerrix-radio.component";
import { CerrixTreeIconViewerComponent } from "@app/shared/atoms/cerrix-tree-icon-viewer/cerrix-tree-icon-viewer.component";

@Component({
    selector: "app-cerrix-select-tree",
    imports: [
        NgTemplateOutlet,
        MatIconModule,
        CerrixCheckboxComponent,
        CerrixRadioComponent,
        CerrixTreeIconViewerComponent,
    ],
    templateUrl: "./cerrix-select-tree.component.html",
    styleUrl: "./cerrix-select-tree.component.scss",
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CerrixSelectTreeComponent {
    readonly items = input.required<CerrixTreeItem[]>();
    protected readonly radioGroupName = `select-tree-${crypto.randomUUID()}`;

    readonly iconPrefix = input("");
    readonly emptyText = input("No items");
    readonly multiselect = input(false, { transform: booleanAttribute });
    readonly includeChildren = input(false, { transform: booleanAttribute });
    readonly showSelectedOnly = input(false, { transform: booleanAttribute });
    readonly selectedIds = input<string[]>([]);
    readonly filter = input("");
    readonly selectionChanged = output<string[]>();

    private readonly expandedGuids = signal<ReadonlySet<string>>(new Set());
    private readonly normalizedFilter = computed(() => this.filter().trim().toLowerCase());

    public selectAll(): void {
        this.selectionChanged.emit(this.allGuids(this.items()));
    }

    public expandAll(): void {
        this.expandedGuids.set(new Set(this.allGuids(this.items())));
    }

    protected toggleExpanded(item: CerrixTreeItem): void {
        const current = new Set(this.expandedGuids());

        if (this.isExpanded(item)) {
            current.delete(item.Guid);
        } else {
            current.add(item.Guid);
        }
        this.expandedGuids.set(current);
    }

    protected toggleSelected(item: CerrixTreeItem): void {
        //single select mode
        if (!this.multiselect()) {
            const selected = this.isSelected(item) ? [] : [item.Guid];
            this.selectionChanged.emit(selected);
            return;
        } else {
            //multi select mode
            const selectedGuids = new Set(this.selectedIds());
            const targetIds = this.includeChildren()
                ? this.getIdOfSelfAndChildren(item)
                : [item.Guid];

            if (this.isSelected(item)) {
                for (const id of targetIds) {
                    selectedGuids.delete(id);
                }
            } else {
                for (const id of targetIds) {
                    selectedGuids.add(id);
                }
            }
            this.selectionChanged.emit([...selectedGuids]);
        }
    }

    private getIdOfSelfAndChildren(item: CerrixTreeItem): string[] {
        const ids = new Set<string>();

        const visit = (node: CerrixTreeItem): void => {
            ids.add(node.Guid);
            node.Children?.forEach(visit);
        };

        visit(item);
        return [...ids];
    }

    protected hasChildren(item: CerrixTreeItem): boolean {
        return !!item.Children?.some((child) => this.isVisible(child));
    }

    protected isExpanded(item: CerrixTreeItem): boolean {
        return this.expandedGuids().has(item.Guid) || this.isAutoExpanded(item);
    }

    protected isVisible(item: CerrixTreeItem): boolean {
        const visibleGuids = this.visibleGuids();
        return visibleGuids === null || visibleGuids.has(item.Guid);
    }

    // While filtering, expand items that only match through a descendant so matches stay visible.
    private isAutoExpanded(item: CerrixTreeItem): boolean {
        if (!this.normalizedFilter() && !this.showSelectedOnly()) {
            return false;
        }

        return !this.matchesVisibilityCriteria(item) && this.isVisible(item);
    }

    protected isSelected(item: CerrixTreeItem): boolean {
        return this.selectedIds().includes(item.Guid);
    }

    private matchesFilter(item: CerrixTreeItem, filter: string): boolean {
        if (!filter) {
            return true;
        }
        return item.Name?.toLowerCase().includes(filter) ?? false;
    }

    private matchesVisibilityCriteria(item: CerrixTreeItem): boolean {
        if (this.showSelectedOnly() && !this.isSelected(item)) {
            return false;
        }

        return this.matchesFilter(item, this.normalizedFilter());
    }

    protected isPartiallySelected(item: CerrixTreeItem): boolean {
        return this.partiallySelectedGuids().has(item.Guid);
    }

    private readonly partiallySelectedGuids = computed((): Set<string> => {
        const selected = new Set(this.selectedIds());
        const partial = new Set<string>();

        const visit = (nodes: CerrixTreeItem[]): boolean => {
            let anySelected = false;

            for (const node of nodes) {
                const childSelected = node.Children?.length ? visit(node.Children) : false;
                if (childSelected && !this.isSelected(node)) {
                    partial.add(node.Guid);
                }

                anySelected = anySelected || childSelected || selected.has(node.Guid);
            }

            return anySelected;
        };

        visit(this.items());
        return partial;
    });

    private readonly visibleGuids = computed<ReadonlySet<string> | null>(() => {
        if (!this.normalizedFilter() && !this.showSelectedOnly()) {
            return null;
        }

        const visible = new Set<string>();
        const visit = (node: CerrixTreeItem): boolean => {
            const selfMatches = this.matchesVisibilityCriteria(node);
            const hasMatchingChild = (node.Children ?? []).reduce(
                (childMatches, child) => visit(child) || childMatches,
                false,
            );

            if (selfMatches || hasMatchingChild) {
                visible.add(node.Guid);
                return true;
            }
            return false;
        };

        this.items().forEach(visit);
        return visible;
    });

    private allGuids = (items: CerrixTreeItem[]): string[] => {
        return items.flatMap((item) => [item.Guid, ...this.allGuids(item.Children ?? [])]);
    };
}
